/* eslint-disable max-lines-per-function, complexity, max-len, indent, react-refresh/only-export-components, no-console */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/EnhancedAuthContext';
import { UserStatus, SystemRole, PermissionScope, UserSource } from '@/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, AlertTriangle, Clock, UserX } from 'lucide-react';
import loadingGif from '@/assets/logos/page loading.gif';

interface EnhancedProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: Array<{
    resource: string;
    action: string;
    scope?: PermissionScope;
  }>;
  requiredRoles?: string[];
  requiredSystemRoles?: SystemRole[];
  requireApproval?: boolean;
  requireActiveStatus?: boolean;
  fallbackPath?: string;
}

export const EnhancedProtectedRoute: React.FC<EnhancedProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  requiredRoles = [],
  requiredSystemRoles = [],
  requireApproval = true,
  requireActiveStatus = true,
  fallbackPath = '/auth'
}) => {
  const {
    user,
    userProfile,
    approvalStatus,
    approvalLoading,
    loading,
    hasPermission,
    hasRole,
    hasSystemRole
  } = useAuth();
  const location = useLocation();

  // Only block on initial auth session check (fast ~100ms)
  if (loading) {
    return <ApprovalLoadingScreen />;
  }

  // Check if user is authenticated
  if (!user) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Check if user needs to set their password (invited via email but hasn't confirmed)
  // This prevents users from bypassing the password setup page by navigating away
  // Only applies to email-based auth (not OAuth users like Google sign-in)
  // OAuth users (source !== EMAIL) are already verified through the OAuth provider
  if (!user.emailVerified && user.source === UserSource.EMAIL) {
    // Only redirect if not already on auth pages
    const isAuthPage = location.pathname.startsWith('/auth');
    if (!isAuthPage) {
      console.log('[EnhancedProtectedRoute] User email not verified, redirecting to set-password');
      return <Navigate to='/auth/set-password' replace />;
    }
  }

  // If profile is loading but we have cached profile with permissions, allow access
  // Only block if NO profile data at all (first time user)
  if (requireApproval && approvalLoading && !userProfile) {
    return <ApprovalLoadingScreen />;
  }

  // If we have approval status, check it
  if (requireApproval && approvalStatus && approvalStatus.status === 'approved') {
    // Continue to other checks below
  } else if (requireApproval && approvalStatus && approvalStatus.status !== 'approved') {
    // Only show approval required if we have an actual non-approved status (pending/rejected)
    return <ApprovalRequired />;
  }

  // Check user status - only block if we have a profile AND status is not active
  // If userProfile is still loading (undefined), don't block - let the loading screen handle it
  if (requireActiveStatus && userProfile && userProfile.status !== UserStatus.ACTIVE) {
    return <UserStatusBlocked userStatus={userProfile.status} />;
  }

  // CRITICAL: Wait for user profile to load before checking permissions/roles
  // This prevents the "Access Denied" flash when profile data is still loading
  // Only show loading if we need to check permissions/roles AND profile isn't loaded yet
  const needsPermissionCheck =
    requiredSystemRoles.length > 0 || requiredRoles.length > 0 || requiredPermissions.length > 0;

  if (needsPermissionCheck && !userProfile) {
    // Profile is still loading - show loading screen instead of Access Denied
    return <ApprovalLoadingScreen />;
  }

  // Check required system roles
  if (requiredSystemRoles.length > 0) {
    const hasRequiredSystemRole = requiredSystemRoles.some(role => hasSystemRole(role));
    if (!hasRequiredSystemRole) {
      return <AccessDenied reason='Insufficient system role privileges' />;
    }
  }

  // Check required roles
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => hasRole(role));
    if (!hasRequiredRole) {
      return <AccessDenied reason='Insufficient role privileges' />;
    }
  }

  // Check required permissions
  if (requiredPermissions.length > 0) {
    const hasRequiredPermission = requiredPermissions.every(permission =>
      hasPermission({
        resource: permission.resource,
        action: permission.action,
        scope: permission.scope || PermissionScope.SELF
      })
    );
    if (!hasRequiredPermission) {
      return <AccessDenied reason='Insufficient permissions' />;
    }
  }

  // All checks passed, render children
  return children;
};

// Approval Loading Screen Component
const ApprovalLoadingScreen: React.FC = () => {
  const messages = [
    'Checking your garage pass...',
    'Revving up your permissions...',
    'Shifting into the right gear...',
    'Tuning your access settings...',
    'Checking under the hood...',
    'Warming up the engine...',
    'Adjusting your mirrors...',
    'Filling up your access tank...',
    'Calibrating your dashboard...',
    'Testing your headlights...',
    'Checking tire pressure...',
    'Syncing your navigation...',
    'Polishing your credentials...',
    'Installing turbo permissions...',
    'Activating nitrous boost...',
    'Checking oil levels...',
    'Aligning your access wheels...',
    'Starting the ignition...',
    'Buckling your security belt...',
    'Adjusting the seat position...',
    'Setting cruise control...',
    'Checking the rearview mirror...',
    'Engaging all-wheel drive...',
    'Activating parking sensors...',
    'Loading your route map...'
  ];

  // Shuffle messages to create random order
  const [shuffledMessages] = React.useState(() => {
    const shuffled = [...messages];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  });

  const [messageIndex, setMessageIndex] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % shuffledMessages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [shuffledMessages.length]);

  return (
    <div className='flex items-center justify-center min-h-screen bg-background'>
      <Card className='w-full max-w-md'>
        <CardContent className='pt-6 text-center space-y-4'>
          <div className='flex justify-center items-center'>
            <img
              src={loadingGif}
              alt='Loading...'
              className='max-w-full max-h-72 w-auto h-auto object-contain'
            />
          </div>
          <p className='text-lg font-medium text-foreground'>{shuffledMessages[messageIndex]}</p>
        </CardContent>
      </Card>
    </div>
  );
};

// Approval Required Component
const ApprovalRequired: React.FC = () => {
  const { approvalStatus } = useAuth();

  return (
    <div className='flex items-center justify-center min-h-screen bg-background'>
      <Card className='w-full max-w-md'>
        <CardHeader className='text-center'>
          <div
            className='mx-auto mb-4 flex h-12 w-12 items-center justify-center 
            rounded-full bg-yellow-100'
          >
            <Clock className='h-6 w-6 text-yellow-600' />
          </div>
          <CardTitle>Account Pending Approval</CardTitle>
          <CardDescription>
            Your account is currently pending approval from an administrator.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {approvalStatus?.status === 'pending' && (
            <div className='text-center'>
              <p className='text-sm text-muted-foreground mb-4'>
                We&apos;ve received your request and will review it shortly. You&apos;ll receive an
                email notification once your account is approved.
              </p>
              <div className='bg-blue-50 border border-blue-200 rounded-md p-3'>
                <p className='text-sm text-blue-800'>
                  <strong>Requested:</strong>{' '}
                  {new Date(approvalStatus.requestedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}

          {approvalStatus?.status === 'rejected' && (
            <div className='text-center'>
              <p className='text-sm text-muted-foreground mb-4'>
                Your account request has been rejected.
              </p>
              {approvalStatus.rejectionReason && (
                <div className='bg-red-50 border border-red-200 rounded-md p-3'>
                  <p className='text-sm text-red-800'>
                    <strong>Reason:</strong> {approvalStatus.rejectionReason}
                  </p>
                </div>
              )}
              <Button className='mt-4' onClick={() => (window.location.href = '/auth')}>
                Try Again
              </Button>
            </div>
          )}

          <div className='text-center'>
            <Button variant='outline' onClick={() => (window.location.href = '/auth')}>
              Back to Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// User Status Blocked Component
const UserStatusBlocked: React.FC<{ userStatus?: UserStatus }> = ({ userStatus }) => {
  const getStatusMessage = (status?: UserStatus) => {
    switch (status) {
      case UserStatus.INACTIVE:
        return {
          title: 'Account Inactive',
          message:
            'Your account has been temporarily deactivated. Please contact your administrator.',
          icon: <UserX className='h-6 w-6 text-muted-foreground' />
        };
      case UserStatus.SUSPENDED:
        return {
          title: 'Account Suspended',
          message:
            'Your account has been suspended. Please contact your administrator for assistance.',
          icon: <AlertTriangle className='h-6 w-6 text-red-600' />
        };
      case UserStatus.DEACTIVATED:
        return {
          title: 'Account Deactivated',
          message: 'Your account has been permanently deactivated.',
          icon: <UserX className='h-6 w-6 text-red-600' />
        };
      default:
        return {
          title: 'Account Access Restricted',
          message: 'Your account status prevents access to this application.',
          icon: <Shield className='h-6 w-6 text-muted-foreground' />
        };
    }
  };

  const statusInfo = getStatusMessage(userStatus);

  return (
    <div className='flex items-center justify-center min-h-screen bg-background'>
      <Card className='w-full max-w-md'>
        <CardHeader className='text-center'>
          <div className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted'>
            {statusInfo.icon}
          </div>
          <CardTitle>{statusInfo.title}</CardTitle>
          <CardDescription>{statusInfo.message}</CardDescription>
        </CardHeader>
        <CardContent className='text-center'>
          <Button variant='outline' onClick={() => (window.location.href = '/auth')}>
            Back to Login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Access Denied Component
const AccessDenied: React.FC<{ reason: string }> = ({ reason }) => {
  return (
    <div className='flex items-center justify-center min-h-screen bg-background'>
      <Card className='w-full max-w-md'>
        <CardHeader className='text-center'>
          <div className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100'>
            <Shield className='h-6 w-6 text-red-600' />
          </div>
          <CardTitle>Access Denied</CardTitle>
          <CardDescription>You don&apos;t have permission to access this resource.</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='bg-red-50 border border-red-200 rounded-md p-3'>
            <p className='text-sm text-red-800'>
              <strong>Reason:</strong> {reason}
            </p>
          </div>

          <div className='text-center space-y-2'>
            <p className='text-sm text-muted-foreground'>
              If you believe this is an error, please contact your administrator.
            </p>
            <div className='flex gap-2 justify-center'>
              <Button variant='outline' onClick={() => window.history.back()}>
                Go Back
              </Button>
              <Button onClick={() => (window.location.href = '/dashboard')}>Dashboard</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Permission-based component wrapper
export const withPermission = <P extends object>(
  Component: React.ComponentType<P>,
  requiredPermissions: Array<{
    resource: string;
    action: string;
    scope?: PermissionScope;
  }>
) => {
  return (props: P) => (
    <EnhancedProtectedRoute requiredPermissions={requiredPermissions}>
      <Component {...props} />
    </EnhancedProtectedRoute>
  );
};

// Role-based component wrapper
export const withRole = <P extends object>(
  Component: React.ComponentType<P>,
  requiredRoles: string[]
) => {
  return (props: P) => (
    <EnhancedProtectedRoute requiredRoles={requiredRoles}>
      <Component {...props} />
    </EnhancedProtectedRoute>
  );
};

// System role-based component wrapper
export const withSystemRole = <P extends object>(
  Component: React.ComponentType<P>,
  requiredSystemRoles: SystemRole[]
) => {
  return (props: P) => (
    <EnhancedProtectedRoute requiredSystemRoles={requiredSystemRoles}>
      <Component {...props} />
    </EnhancedProtectedRoute>
  );
};
